import { useState, useEffect, useRef } from 'react';
import type { ModifiedRow } from '../types';

type UseGridDataProps = {
  gridData: Record<string, unknown>[];
};

type UseGridDataReturn = {
  displayData: Record<string, unknown>[];
  setDisplayData: React.Dispatch<React.SetStateAction<Record<string, unknown>[]>>;
  modifiedRows: Map<string, ModifiedRow>;
  setModifiedRows: React.Dispatch<React.SetStateAction<Map<string, ModifiedRow>>>;
  modifiedCells: Set<string>;
  setModifiedCells: React.Dispatch<React.SetStateAction<Set<string>>>;
  markedForDeletion: Set<string>;
  setMarkedForDeletion: React.Dispatch<React.SetStateAction<Set<string>>>;
  tempIdCounterRef: React.MutableRefObject<number>;
  hasUnsavedChanges: boolean;
  validationErrors: Map<string, string>;
  setValidationErrors: React.Dispatch<React.SetStateAction<Map<string, string>>>;
  hasValidationErrors: boolean;
};

/** Manages display data and tracks inline-edit state (modified rows, cells, deletions) */
export const useGridData = ({ gridData }: UseGridDataProps): UseGridDataReturn => {
  const [displayData, setDisplayData] = useState<Record<string, unknown>[]>(gridData);
  const [modifiedRows, setModifiedRows] = useState<Map<string, ModifiedRow>>(new Map());
  const [modifiedCells, setModifiedCells] = useState<Set<string>>(new Set());
  const [markedForDeletion, setMarkedForDeletion] = useState<Set<string>>(new Set());
  const tempIdCounterRef = useRef(0);
  const [validationErrors, setValidationErrors] = useState<Map<string, string>>(new Map());

  // Sync displayData when gridData changes
  useEffect(() => {
    // Use functional update to access current state without adding to dependencies
    setDisplayData((currentDisplayData) => {
      if (modifiedRows.size === 0 && markedForDeletion.size === 0) {
        return gridData;
      }
      return currentDisplayData;
    });
    // Only depend on gridData - we check modifiedRows/markedForDeletion inside the updater
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridData]);

  const hasUnsavedChanges = modifiedRows.size > 0 || markedForDeletion.size > 0;
  const hasValidationErrors = validationErrors.size > 0;

  return {
    displayData,
    setDisplayData,
    modifiedRows,
    setModifiedRows,
    modifiedCells,
    setModifiedCells,
    markedForDeletion,
    setMarkedForDeletion,
    tempIdCounterRef,
    hasUnsavedChanges,
    validationErrors,
    setValidationErrors,
    hasValidationErrors,
  };
};
